import { ActionTree } from 'vuex'
import { RouteBuilder } from '@/router'
import StoreAPI from '@/util/api/StoreAPI'
import { EditorState } from '@/store/editor/types'
import { RootState } from '@/store/types'
import { FETCH_UPDATES, FETCH_ENTRY, REMOVE_ENTRY, SET_ENTRY } from '@/store/entries/constants'
import Entry from '@/data/entry-model'
import { main } from '@/main'
import { REVISION_VIEW, REVISION_LOAD_INITIAL } from '@/store/revision/constants'
import { PEER_LOAD } from '@/store/share/constants'
import EntryRepository from '@/data/entry-repository'
import {
    RESET_EDITOR,
    ENTRY_LOAD_DATA,
    EDITOR_CLEAR,
    EDITOR_SET_SCHEMA,
    EDITOR_SET_DATA,
    ENTRY_REFRESH,
    ENTRY_LOAD,
    ENTRY_UPDATE,
    EDITOR_SET_ERRORS,
    EDITOR_MARK_SYNCING,
    EDITOR_MARK_SYNCED,
    EDITOR_RESET_STATUSES,
    ENTRY_INSERT,
    ENTRY_DELETE,
    EDITOR_LOAD_TAG_OPTIONS,
    EDITOR_SET_TAG_OPTIONS,
    ENTRY_UPDATE_MODEL
} from './constants'

const actions: ActionTree<EditorState, RootState> = {
    /**
     * Load data into the editor, refreshing and updating
     * schema first, then also loading peers
     */
    [ENTRY_LOAD_DATA]({ commit, dispatch }, data) {
        if (!data) {
            // TODO: 404 page ?
            dispatch(RESET_EDITOR)
            return
        }
        commit(EDITOR_CLEAR)
        commit(REVISION_VIEW, null)
        commit(EDITOR_SET_SCHEMA, data.schemaType)
        commit(EDITOR_SET_DATA, data)
        dispatch(PEER_LOAD)
    },

    /**
     * Refresh data in the editor
     */
    [ENTRY_REFRESH]({ commit }, data) {
        commit(EDITOR_SET_DATA, data)
    },

    /**
     * Load an entry from the API
     */
    async [ENTRY_LOAD]({ dispatch }, entryId: string) {
        RouteBuilder.setEditRoute(entryId)
        dispatch(ENTRY_LOAD_DATA, await dispatch(FETCH_ENTRY, entryId))
    },

    /**
     * Copy changes from the Vuex state into the model
     */
    async [ENTRY_UPDATE_MODEL]({ state, rootState, rootGetters }): Promise<Entry> {
        return (state.entry.model || EntryRepository.create(state.entry.schema))
            .connect(rootState, rootGetters)
            .setData({
                parentId: state.entry.vault,
                tags: state.entry.tags,
                icon: state.entry.icon,
                title: state.entry.fields.title.value || '',
                permission: state.entry.permission
            })
            .setFields(
                Object.keys(state.entry.fields)
                    .filter(key => key !== 'title')
                    .reduce((fields, fieldKey) => ({
                        ...fields,
                        [fieldKey]: state.entry.fields[fieldKey].value || ''
                    }), {})
            )
    },

    /**
     * Sync the editor entry to the server
     */
    async [ENTRY_UPDATE]({
        commit, dispatch, getters
    }, message?: string) {
        if (getters.hasErrors) {
            commit(EDITOR_SET_ERRORS, getters.entryErrors)
            main.$error({ text: 'Validation error when attempting to sync entry' })
            main.$error({
                text: JSON.stringify(getters.entryErrors)
            })
            return
        }
        commit(EDITOR_MARK_SYNCING)
        try {
            const model: Entry = await dispatch(ENTRY_UPDATE_MODEL)
            // this makes the UI look really snappy :)
            commit(SET_ENTRY, model)
            // do the rest of the update at any point,
            // so the user can return to the UI
            setTimeout(async () => {
                await model.save()
                commit(EDITOR_MARK_SYNCED)
                commit(EDITOR_SET_DATA, model)
                dispatch(FETCH_UPDATES)
                dispatch(REVISION_LOAD_INITIAL)
                main.$info({ text: 'Entry synced' })
            })
        } catch (err) {
            console.error(err)
            main.$error({ text: JSON.stringify(err) })
        }
        setTimeout(() => commit(EDITOR_RESET_STATUSES), 2000)
    },

    /**
     * Send a new editor entry to the server
     */
    async [ENTRY_INSERT]({
        commit, dispatch, getters
    }) {
        if (getters.hasErrors) {
            commit(EDITOR_SET_ERRORS, getters.entryErrors)
            return
        }
        const model: Entry = await dispatch(ENTRY_UPDATE_MODEL)
        try {
            const serverModel = await model.save()
            commit(EDITOR_SET_DATA, serverModel)
            dispatch(FETCH_UPDATES)
            main.$info({ text: 'Entry created' })
            RouteBuilder.setEditRoute(serverModel.id!)
        } catch (err) {
            console.error(err)
        }
    },

    /**
     * Delete the editor entry on the server
     */
    async [ENTRY_DELETE]({ state, commit, dispatch }): Promise<void> {
        await StoreAPI.delete(state.entry.id!)
        // remove from UI instantly
        commit(REMOVE_ENTRY, state.entry.id)
        dispatch(RESET_EDITOR)
        // permanently save delete
        dispatch(FETCH_UPDATES)
    },

    /**
     * Clear editor and set to default schema
     */
    [RESET_EDITOR]({ commit }) {
        RouteBuilder.setEditRoute()
        commit(EDITOR_SET_SCHEMA, 'website')
        commit(EDITOR_CLEAR)
    },

    async [EDITOR_LOAD_TAG_OPTIONS]({ commit }) {
        commit(EDITOR_SET_TAG_OPTIONS, await StoreAPI.getTags())
    }
}

export default actions
